import YoutubePlayer from "youtube-player";
import VimeoPlayer from "@vimeo/player";

const mobileMQ = window.matchMedia("(max-width: 1199px)");

declare global {
  interface Window {
    dataLayer: any[];
  }
}

export class VideoEmbed {
  private element: HTMLElement;
  private videoSource: string | null;
  private media: HTMLElement | null;
  private youtubeMedia: HTMLElement | null;
  private youTubePlayer: any;
  private vimeoplayer: any;
  private vimeoMedia: HTMLElement | null;
  private playButton: HTMLButtonElement | null;
  private localCDNMedia: HTMLVideoElement | null;
  private localCDNMediaWallpaperCreated: boolean = false;
  private localCDNMediaInner: HTMLElement | null;
  private eventDataSent: boolean = false;

  constructor(element: HTMLElement) {
    if (!!element) {
      this.element = element;
      this.videoSource = this.element.getAttribute("data-video-source");
      this.media = this.element.querySelector(".video-embed__media-wrap");
      this.youtubeMedia = this.element.querySelector(".video-embed__video--yt");
      this.vimeoMedia = this.element.querySelector(
        ".video-embed__video--vimeo"
      );
      this.localCDNMedia = this.element.querySelector(
        ".video-embed__video--native"
      );
      this.localCDNMediaInner = this.element.querySelector(
        ".video-embed__media-wrap-inner"
      );
      this.playButton = this.element.querySelector(".video-embed__button");
      this.init();
    }
  }

  // MARK: Init
  private init() {
    this.handleResize();
    this.pauseVideoWhenAriaHidden();
  }

  // MARK: Handle Resize
  private handleResize() {
    const resize = () => {
      if (this.videoSource === "youtube") {
        this.handleYouTube();
      } else if (this.videoSource === "vimeo") {
        this.handleVimeo();
      } else {
        this.handleVideoTag();
      }
    };
    mobileMQ.addEventListener("change", resize);
    resize();
  }

  // MARK: Create Youtube Player
  private createYouTubePlayer() {
    const videoId = this.youtubeMedia?.dataset.vid;

    if (!this.youtubeMedia) {
      console.error("Media element is not defined.");
      return; // Exit the function if media is not defined
    }

    this.youTubePlayer = YoutubePlayer(this.youtubeMedia, {
      videoId,
      playerVars: {
        rel: 0,
        enablejsapi: 1,
      },
    });
    this.youTubePlayer.on("stateChange", (event: any) => {
      if (event.data == 1) {
        this.eventTracking(this.youTubePlayer);
      }
    });
  }

  // MARK: Handle Youtube
  private handleYouTube() {
    if (this.youTubePlayer) {
      this.youTubePlayer.destroy();
    }

    if (mobileMQ.matches) {
      this.createYouTubePlayer();
    } else {
      this.playButton?.addEventListener(
        "click",
        () => {
          this.createYouTubePlayer();
          this.youTubePlayer.on("ready", () => {
            this.youTubePlayer.playVideo();
          });
          this.youTubePlayer.on("stateChange", (event: any) => {
            if (event.data == 0) {
              this.youTubePlayer.destroy();
            }
          });
        },
        { once: true }
      );
    }
  }

  // MARK: Create Vimeo
  private createVimeoPlayer() {
    const videoId = this.vimeoMedia?.dataset.vimeoVideoId;

    if (!this.vimeoMedia) {
      console.error("Media element is not defined.");
      return; // Exit the function if media is not defined
    }

    this.vimeoplayer = new VimeoPlayer(this.vimeoMedia, {
      id: Number(videoId),
    });
    // Push Event to Google Tag Manager
    this.vimeoplayer.on("playing", () => {
      this.eventTracking(this.vimeoplayer);
    });
  }

  // MARK: Handle Vimeo
  private handleVimeo() {
    if (this.vimeoplayer) {
      this.vimeoplayer.destroy();
    }

    if (mobileMQ.matches) {
      this.createVimeoPlayer();
    } else {
      this.playButton?.addEventListener("click", () => {
        this.createVimeoPlayer();
        this.vimeoplayer.play();
        this.vimeoplayer.on("ended", () => {
          this.vimeoplayer.destroy();
        });
      });
    }
  }
  // MARK: Handle Video Tag
  private handleVideoTag() {
    if (
      this.localCDNMedia?.classList.contains(
        "video-embed__video--native--no-wallpaper"
      ) &&
      !this.localCDNMediaWallpaperCreated
    ) {
      this.createWallpaperForVideo();
    }

    if (!mobileMQ.matches) {
      this.localCDNMediaInner?.setAttribute("aria-hidden", "true");
      this.playButton?.addEventListener("click", () => {
        this.localCDNMediaInner?.setAttribute("aria-hidden", "false");
        this.localCDNMedia?.play();
        this.localCDNMedia?.addEventListener("play", () => {
          this.media?.classList.add("video-playing");
        });
        this.localCDNMedia?.addEventListener("ended", () => {
          this.media?.classList.remove("video-playing");
          this.localCDNMediaInner?.setAttribute("aria-hidden", "true");
        });
      });
    } else {
      this.localCDNMediaInner?.setAttribute("aria-hidden", "false");
    }

    this.localCDNMedia?.addEventListener("play", () => {
      this.eventTracking(this.localCDNMedia);
    });
  }

  // MARK: Create Wallpaper for Video Tag
  private async createWallpaperForVideo() {
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");

    if (!ctx) return;

    // Ensure metadata is loaded
    if (this.localCDNMedia) {
      if (this.localCDNMedia.readyState < 2) {
        // HAVE_CURRENT_DATA (2) ensures at least some data is available
        await new Promise<void>((resolve) => {
          this.localCDNMedia?.addEventListener("loadeddata", () => resolve(), {
            once: true,
          });
        });
      }

      canvas.width = this.localCDNMedia.videoWidth;
      canvas.height = this.localCDNMedia.videoHeight;

      this.localCDNMedia.currentTime = 1;

      await new Promise<void>((resolve) => {
        this.localCDNMedia?.addEventListener("seeked", () => resolve(), {
          once: true,
        });
      });

      ctx.drawImage(this.localCDNMedia, 0, 0, canvas.width, canvas.height);

      const imageData = canvas.toDataURL("image/png");

      const imageElement = document.createElement("img");
      imageElement.src = imageData;
      imageElement.alt =
        this.localCDNMedia.getAttribute("title") || "Wallpaper";

      this.media?.insertBefore(imageElement, this.localCDNMediaInner);
      this.localCDNMediaWallpaperCreated = true;
    }
  }

  // MARK: Observe ARIA Hidden
  private pauseVideoWhenAriaHidden() {
    const observerCallback = (mutationsList: MutationRecord[]) => {
      for (const mutation of mutationsList) {
        if (
          mutation.type === "attributes" &&
          (mutation.attributeName === "aria-hidden" ||
            mutation.attributeName === "hidden")
        ) {
          if (this.element?.closest('[aria-hidden="true"], [hidden]')) {
            // If YouTube - pause
            if (this.youTubePlayer) {
              this.youTubePlayer.pauseVideo();
            }

            // If Vimeo - pause
            if (this.vimeoplayer) {
              this.vimeoplayer.pause();
            }

            // If Local Video - pause
            if (this.localCDNMedia) {
              this.localCDNMedia.pause();
            }
          }
        }
      }
    };

    // Observe both aria-hidden and hidden attributes
    const observerConfig = {
      attributes: true,
      subtree: true,
      attributeFilter: ["aria-hidden", "hidden"],
    };

    const observer = new MutationObserver(observerCallback);
    observer.observe(document.body, observerConfig);
  }

  // MARK: Event Tracking
  private eventTracking(source: any) {
    try {
      const dataLayer = window.dataLayer || window.parent.dataLayer;

      if (source === this.youTubePlayer && !this.eventDataSent) {
        if (dataLayer) {
          dataLayer.push({
            event: "video_start",
            video_url:
              "https://www.youtube.com/embed/" + this.youtubeMedia?.dataset.vid,
            video_title: this.youtubeMedia?.title || "YouTube Video",
            video_provider: "youtube",
          });
        }
      } else if (source === this.vimeoplayer && !this.eventDataSent) {
        if (dataLayer) {
          dataLayer.push({
            event: "video_start",
            video_url:
              "https://player.vimeo.com/video/" +
              this.vimeoMedia?.dataset.vimeoVideoId,
            video_title: this.vimeoMedia?.title || "Vimeo Video",
            video_provider: "vimeo",
          });
        }
      } else if (source === this.localCDNMedia && !this.eventDataSent) {
        if (dataLayer) {
          dataLayer.push({
            event: "video_start",
            video_url: this.localCDNMedia?.currentSrc,
            video_title: "Your Video Title Here",
            video_provider: "local",
          });
        }
      }
      this.eventDataSent = true;
    } catch (error) {
      console.error("Error in event tracking:", error);
      if (typeof window.dataLayer === "undefined") {
        window.dataLayer = [];
        this.eventTracking(source);
      }
    }
  }
}

export default function videoEmbedInit() {
  const videoEmbed = document.querySelectorAll(
    ".video-embed"
  ) as NodeListOf<HTMLElement>;
  for (let i = 0; i < videoEmbed.length; i++) {
    new VideoEmbed(videoEmbed[i]);
  }
}
